/**
 * Live API
 *
 * Provides Live/Camera-related API functions.
 */

import {
  CameraDetailResponseType,
  cameraMapType,
  getAllCamerasResponseType,
  videoMapResponseType,
} from '../../types/apiResponse/liveTypes'
import { getData, postData } from '../../utils'
import { toast } from '@/utils/toast'

/**
 * Updates video map configuration
 *
 * @deprecated This endpoint has been removed from backend V2
 */
export const postLive = async (
  _cameraMap: cameraMapType,
  _setIsUpdated: () => void,
  _onClose: () => void,
) => {
  console.warn('postLive is deprecated: /update-video-map endpoint no longer exists')
  toast.error('This feature is no longer available')
}

/**
 * Fetches all cameras
 *
 * Backend endpoint: GET /camera-set/all
 *
 * @param setAllCameras - Callback to update state with all cameras data
 *
 * @example
 * ```typescript
 * getAllCameras((cameras) => {
 *   console.log(`Found ${cameras.length} cameras`)
 * })
 * ```
 */
export const getAllCameras = async (
  setAllCameras: (allCameras: getAllCamerasResponseType) => void,
) => {
  getData<getAllCamerasResponseType>('/camera')
    .then((response) => {
      setAllCameras(response)
    })
    .catch(() => {
      toast.error('ERROR!!')
    })
}

/**
 * Fetches video map configuration
 *
 * @deprecated This endpoint has been removed from backend V2
 */
export const getVideoMap = (_setVideoMap: (videoResponse: videoMapResponseType) => void) => {
  console.warn('getVideoMap is deprecated: /video-map endpoint no longer exists')
}

/**
 * Camera item from API (already unwrapped by axios interceptor)
 */
type TankCameraItem = {
  calibration_params: string
  position: 'TOP' | 'LEFT' | 'RIGHT'
  camera: {
    id: string
    model: string
    frame_rate: number
    resolution: string
    hls_uri: string
  }
}

/**
 * Transforms API response to CameraDetailResponseType
 * Note: axios interceptor unwraps { success, data } → data (array)
 */
const transformCameraResponse = (
  tankId: string,
  cameras: TankCameraItem[],
): CameraDetailResponseType => {
  const result: CameraDetailResponseType = {
    cameraSetId: '',
    tankId: tankId,
    leftCamera: null,
    rightCamera: null,
    topCamera: null,
  }

  if (Array.isArray(cameras)) {
    cameras.forEach((item) => {
      const cameraLive = {
        cameraId: item.camera.id,
        hlsUri: item.camera.hls_uri,
      }

      switch (item.position.toUpperCase()) {
        case 'LEFT':
          result.leftCamera = cameraLive
          break
        case 'RIGHT':
          result.rightCamera = cameraLive
          break
        case 'TOP':
          result.topCamera = cameraLive
          break
      }
    })
  }

  return result
}

/**
 * Fetches camera detail for a specific tank
 *
 * Backend endpoint: GET /tank/{tankId}/cameras
 *
 * @param tankId - Tank ID to fetch camera detail for
 * @param setCameraDetail - Callback to update state with camera detail (or null if not found)
 *
 * @example
 * ```typescript
 * getCameraDetail('tank-123', (detail) => {
 *   if (detail) {
 *     console.log(`Camera: ${detail.tankName}`)
 *   }
 * })
 * ```
 */
export const getCameraDetail = (
  tankId: string,
  setCameraDetail: (cameraDetail: CameraDetailResponseType | null) => void,
) => {
  getData<TankCameraItem[]>(`tank/${tankId}/cameras`)
    .then((cameras) => {
      const transformed = transformCameraResponse(tankId, cameras)
      // Only set if at least one camera exists
      if (transformed.leftCamera || transformed.rightCamera || transformed.topCamera) {
        setCameraDetail(transformed)
      } else {
        setCameraDetail(null)
      }
    })
    .catch(() => setCameraDetail(null))
}

